export const translations = {
  en: {
    nav: {
      home: "Home", about: "About", treatments: "Treatments", conditions: "Conditions",
      journey: "Patient Journey", international: "International Patients",
      science: "Science & Education", faq: "FAQ", contact: "Contact",
      book: "Request a Consultation",
    },
    hero: {
      eyebrow: "Regenerative Medicine · Longevity · Tijuana, Mexico",
      line1: "Revive", line2: "Your Life.",
      secondary: "Advanced Regenerative Medicine. Personalized for You.",
      supporting: "At Revive, we combine science with compassion — because healing isn't about treatment, it's about life.",
      slogan: "Real Science. Real Results. Revive Your Life.",
      ctaPrimary: "Request a Consultation",
      ctaSecondary: "Chat With Our Patient Care Team",
      scroll: "Scroll to explore",
    },
    intro: {
      chapter: "01 — Philosophy",
      title: "Medicine Designed Around You.",
      p1: "Revive takes a deeply personalized approach to regenerative medicine. Our patients are never asked to simply select a treatment from a menu.",
      p2: "Every patient's medical history, goals, condition, laboratory findings and individual needs may be different. Treatment protocols are developed only after a thorough medical evaluation.",
      p3: "Treatment recommendations and associated costs are determined after consultation, because every protocol is individualized.",
      highlight: "No two patients are the same. Their treatment shouldn't be either.",
    },
    why: {
      chapter: "02 — Why Revive",
      title: "A different standard of care.",
      pillars: [
        { t: "Personalized Medicine", d: "Protocols are developed around the individual rather than a one-size-fits-all approach." },
        { t: "Regenerative Science", d: "We evaluate advances in regenerative medicine and cellular therapies while maintaining an evidence-guided medical approach." },
        { t: "Integrated Protocols", d: "When medically appropriate, complementary therapies such as peptides, NAD+ and supportive protocols may be incorporated into an overall strategy." },
        { t: "Compassionate Care", d: "Advanced medicine should never lose the human element. Patients should understand their options and the reasoning behind their care." },
      ],
    },
    treatments: {
      chapter: "03 — Treatments",
      title: "Physician-directed regenerative protocols.",
      note: "Individual results vary. A medical evaluation is required to determine candidacy and appropriate treatment options.",
      items: [
        { t: "IV Mesenchymal Stem Cell Therapy", tag: "Regenerative Medicine", d: "A regenerative approach studied for its potential cellular signaling mechanisms. Candidacy and protocols are individualized following medical evaluation. We clearly distinguish established evidence from emerging or investigational applications." },
        { t: "Orthopedic Regenerative Medicine", tag: "Musculoskeletal", d: "Targeted, image-guided regenerative approaches, where applicable, for the knee, shoulder, hip, joint conditions, sports injuries and musculoskeletal recovery." },
      ],
    },
    peptides: {
      chapter: "04 — Peptide Medicine",
      title: "Precision Peptide Protocols",
      p1: "Peptides are signaling molecules involved in numerous biological processes. At Revive, peptide protocols may be considered as part of a broader physician-directed treatment strategy when medically appropriate.",
      p2: "Availability and suitability depend on physician evaluation, current evidence, regulatory considerations and the individual patient. Not all peptides are approved medications, and emerging peptides may not have established clinical efficacy.",
      quote: "We don't chase trends. We evaluate emerging science.",
      areas: ["Recovery", "Metabolic optimization", "Body composition", "Cellular signaling", "Performance", "Healthy aging", "Sleep and recovery", "Tissue support"],
    },
    nad: {
      chapter: "05 — Cellular Optimization",
      title: "NAD+ & Cellular Optimization",
      p1: "NAD+ plays a central role in cellular metabolism, mitochondrial function and energy pathways.",
      p2: "NAD+ therapy may be offered as part of individualized wellness or optimization protocols when medically appropriate. We avoid exaggerated anti-aging claims and focus on responsible, evidence-guided care.",
    },
    emerging: {
      title: "The future of medicine is evolving.",
      p1: "Regenerative medicine is a rapidly developing field. Revive follows new scientific research, technologies and therapeutic approaches that may show potential benefits.",
      philosophy: "Innovation without abandoning evidence.",
      p2: "Promising or emerging research does not automatically mean a therapy has been proven to treat or cure a condition. We evaluate emerging approaches based on available scientific evidence, patient circumstances, safety and medical appropriateness.",
      marquee: ["Regenerative Science", "Cellular Biology", "Peptide Medicine", "NAD+ Optimization", "Longevity", "Metabolic Health"],
    },
    conditions: {
      chapter: "06 — Patient Goals",
      title: "What brings patients to Revive?",
      sub: "Rather than claiming to cure diseases, we describe the concerns and goals for which patients may seek consultation.",
      cta: "Explore Your Options",
      list: ["Joint discomfort", "Knee problems", "Shoulder problems", "Sports injuries", "Musculoskeletal recovery", "Recovery optimization", "Chronic inflammation concerns", "Fatigue", "Reduced energy", "Metabolic health", "Healthy aging", "Performance optimization", "Longevity", "Overall wellness"],
    },
    method: {
      chapter: "07 — The Revive Method",
      title: "A considered path to personalized care.",
      steps: [
        { n: "01", t: "Consultation", d: "Understand the patient's medical history, concerns and goals." },
        { n: "02", t: "Evaluation", d: "Review appropriate laboratory testing, imaging, medical history and previous treatments." },
        { n: "03", t: "Personalized Protocol", d: "The medical team develops an individualized treatment strategy." },
        { n: "04", t: "Treatment", d: "The patient receives their physician-directed regenerative or optimization protocol." },
        { n: "05", t: "Follow-Up & Optimization", d: "Monitor progress and adjust supportive strategies when medically appropriate." },
      ],
    },
    financing: {
      chapter: "08 — Financing",
      title: "Your health is an investment.",
      p1: "Treatment plans at Revive are individualized, and therefore prices are not published online. Following consultation and evaluation, patients receive information regarding their recommended treatment plan and associated costs.",
      highlight: "Flexible financing options may be available through an independent third-party financing provider for qualifying patients.",
      p2: "Financing is provided by an outside third party and is subject to that provider's eligibility requirements, terms and approval. Revive does not guarantee financing approval.",
      cta: "Explore Your Options",
    },
    booking: {
      chapter: "10 — Book",
      title: "Request a Consultation",
      sub: "Tell us how to reach you. A member of the REVIVE care team will contact you to arrange your consultation.",
      typeLabel: "Consultation Type",
      inPerson: "In-Person Consultation",
      virtual: "Virtual Consultation",
      firstName: "First Name", lastName: "Last Name", email: "Email", phone: "Phone Number",
      country: "Country", contactMethod: "Preferred Contact Method",
      reason: "Reason for Consultation", treatment: "Treatment / Service of Interest",
      date: "Preferred Date", time: "Preferred Time",
      contactOptions: ["Email", "Phone", "WhatsApp"],
      treatmentOptions: ["Regenerative Medicine", "Stem Cell Consultation", "Orthopedic Regenerative Medicine", "Peptide Optimization", "NAD+", "Longevity & Wellness", "Metabolic Optimization", "Not Sure / I Need Guidance"],
      consent: "I consent to being contacted by Revive regarding my consultation request.",
      submit: "Request a Consultation",
      submitting: "Sending…",
      successTitle: "Thank you for contacting Revive.",
      successBody: "A member of the REVIVE care team will contact you to arrange your consultation.",
      waPrompt: "Prefer WhatsApp? Chat With Us Now.",
      required: "Please complete the required fields and consent.",
      selectPlaceholder: "Select…",
    },
    intl: {
      chapter: "International Patients",
      title: "Visiting Revive from the United States",
      p1: "Revive is located in Tijuana, Baja California, and welcomes patients traveling from the United States and internationally.",
      steps: ["Consultation", "Medical review", "Treatment planning", "Travel coordination", "Treatment", "Follow-up"],
      cta: "Speak With Our Patient Care Team",
    },
    footer: {
      slogan: "Real Science. Real Results. Revive Your Life.",
      brandLine: "At Revive, we combine science with compassion.",
      healingLine: "Because healing isn't about treatment. It's about life.",
      clinic: "Revive Regenerative Medical Clinic",
      location: "Tijuana, Baja California, Mexico",
      hours: "Clinic Hours",
      hoursPlaceholder: "Monday – Saturday · 9:00 AM – 5:00 PM",
      addressPlaceholder: "Central Torreo · Av. Santa María, Calete, Int. 402",
      quickLinks: "Explore",
      legal: "Legal",
      privacy: "Privacy Policy", terms: "Terms of Use", cookies: "Cookie Notice", disclaimerLink: "Medical Disclaimer",
      messageWa: "Message on WhatsApp",
      directions: "How to Find Us",
      directionsSub: "A short video guide to reaching the clinic.",
      disclaimer: "Information provided on this website is for educational and informational purposes and is not intended as a substitute for professional medical advice, diagnosis or treatment. Treatment recommendations are made following individual medical evaluation. Outcomes vary between patients.",
      entities: "REVIVE Regenerative Medical Clinic is operated in Mexico by REVIVE REGENERATIVE MEDICAL CLINIC, S. DE R.L. DE C.V. Communications and certain administrative services may be provided by REVIVE REGENERATIVE PARTNERSHIP LLC.",
      rights: "All rights reserved.",
    },
    wa: { desktop: "Chat with Revive" },
    mobileCta: { book: "Request a Consultation", wa: "WhatsApp" },
  },
  es: {
    nav: {
      home: "Inicio", about: "Nosotros", treatments: "Tratamientos", conditions: "Condiciones",
      journey: "Ruta del Paciente", international: "Pacientes Internacionales",
      science: "Ciencia y Educación", faq: "Preguntas", contact: "Contacto",
      book: "Solicitar una consulta",
    },
    hero: {
      eyebrow: "Medicina Regenerativa · Longevidad · Tijuana, México",
      line1: "Revive", line2: "Tu Vida.",
      secondary: "Medicina Regenerativa Avanzada. Personalizada para Ti.",
      supporting: "En Revive combinamos la ciencia con la compasión, porque sanar no se trata del tratamiento, se trata de la vida.",
      slogan: "Ciencia Real. Resultados Reales. Revive Tu Vida.",
      ctaPrimary: "Solicitar una consulta",
      ctaSecondary: "Habla con Nuestro Equipo de Atención",
      scroll: "Desliza para explorar",
    },
    intro: {
      chapter: "01 — Filosofía",
      title: "Medicina Diseñada en Torno a Ti.",
      p1: "Revive adopta un enfoque profundamente personalizado hacia la medicina regenerativa. A nuestros pacientes nunca se les pide simplemente elegir un tratamiento de un menú.",
      p2: "El historial médico, los objetivos, la condición, los hallazgos de laboratorio y las necesidades individuales de cada paciente pueden ser diferentes. Los protocolos de tratamiento se desarrollan únicamente después de una evaluación médica completa.",
      p3: "Las recomendaciones de tratamiento y sus costos se determinan tras la consulta, porque cada protocolo es individualizado.",
      highlight: "No hay dos pacientes iguales. Su tratamiento tampoco debería serlo.",
    },
    why: {
      chapter: "02 — Por Qué Revive",
      title: "Un estándar de atención diferente.",
      pillars: [
        { t: "Medicina Personalizada", d: "Los protocolos se desarrollan en torno a cada persona, en lugar de un enfoque único para todos." },
        { t: "Ciencia Regenerativa", d: "Evaluamos los avances en medicina regenerativa y terapias celulares manteniendo un enfoque médico guiado por la evidencia." },
        { t: "Protocolos Integrados", d: "Cuando es médicamente apropiado, se pueden incorporar terapias complementarias como péptidos, NAD+ y protocolos de apoyo dentro de una estrategia general." },
        { t: "Atención Compasiva", d: "La medicina avanzada nunca debe perder el elemento humano. Los pacientes deben comprender sus opciones y el razonamiento detrás de su atención." },
      ],
    },
    treatments: {
      chapter: "03 — Tratamientos",
      title: "Protocolos regenerativos dirigidos por médicos.",
      note: "Los resultados individuales varían. Se requiere una evaluación médica para determinar la candidatura y las opciones de tratamiento apropiadas.",
      items: [
        { t: "Terapia IV con Células Madre Mesenquimales", tag: "Medicina Regenerativa", d: "Un enfoque regenerativo estudiado por sus posibles mecanismos de señalización celular. La candidatura y los protocolos son individualizados tras una evaluación médica. Distinguimos claramente la evidencia establecida de las aplicaciones emergentes o de investigación." },
        { t: "Medicina Regenerativa Ortopédica", tag: "Musculoesquelético", d: "Enfoques regenerativos dirigidos y guiados por imagen, cuando corresponde, para rodilla, hombro, cadera, condiciones articulares, lesiones deportivas y recuperación musculoesquelética." },
      ],
    },
    peptides: {
      chapter: "04 — Medicina de Péptidos",
      title: "Protocolos de Péptidos de Precisión",
      p1: "Los péptidos son moléculas de señalización involucradas en numerosos procesos biológicos. En Revive, los protocolos de péptidos pueden considerarse como parte de una estrategia de tratamiento más amplia dirigida por médicos, cuando es médicamente apropiado.",
      p2: "La disponibilidad y la idoneidad dependen de la evaluación médica, la evidencia actual, las consideraciones regulatorias y el paciente individual. No todos los péptidos son medicamentos aprobados, y los péptidos emergentes pueden no tener eficacia clínica establecida.",
      quote: "No seguimos las modas. Evaluamos la ciencia emergente.",
      areas: ["Recuperación", "Optimización metabólica", "Composición corporal", "Señalización celular", "Rendimiento", "Envejecimiento saludable", "Sueño y recuperación", "Soporte de tejidos"],
    },
    nad: {
      chapter: "05 — Optimización Celular",
      title: "NAD+ y Optimización Celular",
      p1: "El NAD+ desempeña un papel central en el metabolismo celular, la función mitocondrial y las vías de energía.",
      p2: "La terapia con NAD+ puede ofrecerse como parte de protocolos individualizados de bienestar u optimización cuando es médicamente apropiado. Evitamos afirmaciones exageradas antienvejecimiento y nos enfocamos en una atención responsable y guiada por la evidencia.",
    },
    emerging: {
      title: "El futuro de la medicina está evolucionando.",
      p1: "La medicina regenerativa es un campo en rápido desarrollo. Revive sigue las nuevas investigaciones científicas, tecnologías y enfoques terapéuticos que puedan mostrar beneficios potenciales.",
      philosophy: "Innovación sin abandonar la evidencia.",
      p2: "La investigación prometedora o emergente no significa automáticamente que una terapia haya demostrado tratar o curar una condición. Evaluamos los enfoques emergentes con base en la evidencia científica disponible, las circunstancias del paciente, la seguridad y la idoneidad médica.",
      marquee: ["Ciencia Regenerativa", "Biología Celular", "Medicina de Péptidos", "Optimización NAD+", "Longevidad", "Salud Metabólica"],
    },
    conditions: {
      chapter: "06 — Objetivos del Paciente",
      title: "¿Qué trae a los pacientes a Revive?",
      sub: "En lugar de afirmar que curamos enfermedades, describimos las inquietudes y objetivos por los que los pacientes pueden buscar una consulta.",
      cta: "Explora Tus Opciones",
      list: ["Molestias articulares", "Problemas de rodilla", "Problemas de hombro", "Lesiones deportivas", "Recuperación musculoesquelética", "Optimización de la recuperación", "Inquietudes de inflamación crónica", "Fatiga", "Baja energía", "Salud metabólica", "Envejecimiento saludable", "Optimización del rendimiento", "Longevidad", "Bienestar general"],
    },
    method: {
      chapter: "07 — El Método Revive",
      title: "Un camino cuidadoso hacia la atención personalizada.",
      steps: [
        { n: "01", t: "Consulta", d: "Comprender el historial médico, las inquietudes y los objetivos del paciente." },
        { n: "02", t: "Evaluación", d: "Revisar los estudios de laboratorio, imágenes, historial médico y tratamientos previos apropiados." },
        { n: "03", t: "Protocolo Personalizado", d: "El equipo médico desarrolla una estrategia de tratamiento individualizada." },
        { n: "04", t: "Tratamiento", d: "El paciente recibe su protocolo regenerativo o de optimización dirigido por un médico." },
        { n: "05", t: "Seguimiento y Optimización", d: "Monitorear el progreso y ajustar las estrategias de apoyo cuando sea médicamente apropiado." },
      ],
    },
    financing: {
      chapter: "08 — Financiamiento",
      title: "Tu salud es una inversión.",
      p1: "Los planes de tratamiento en Revive son individualizados, y por lo tanto los precios no se publican en línea. Tras la consulta y evaluación, los pacientes reciben información sobre su plan de tratamiento recomendado y los costos asociados.",
      highlight: "Pueden estar disponibles opciones de financiamiento flexibles a través de un proveedor de financiamiento externo independiente para pacientes que califiquen.",
      p2: "El financiamiento es proporcionado por un tercero externo y está sujeto a los requisitos de elegibilidad, términos y aprobación de dicho proveedor. Revive no garantiza la aprobación del financiamiento.",
      cta: "Explora Tus Opciones",
    },
    booking: {
      chapter: "10 — Agendar",
      title: "Solicitar una consulta",
      sub: "Indícanos cómo contactarte. Un miembro del equipo de atención de REVIVE te contactará para coordinar tu consulta.",
      typeLabel: "Tipo de Consulta",
      inPerson: "Consulta Presencial",
      virtual: "Consulta Virtual",
      firstName: "Nombre", lastName: "Apellido", email: "Correo Electrónico", phone: "Teléfono",
      country: "País", contactMethod: "Método de Contacto Preferido",
      reason: "Motivo de la Consulta", treatment: "Tratamiento / Servicio de Interés",
      date: "Fecha Preferida", time: "Hora Preferida",
      contactOptions: ["Correo", "Teléfono", "WhatsApp"],
      treatmentOptions: ["Medicina Regenerativa", "Consulta de Células Madre", "Medicina Regenerativa Ortopédica", "Optimización con Péptidos", "NAD+", "Longevidad y Bienestar", "Optimización Metabólica", "No estoy seguro / Necesito orientación"],
      consent: "Doy mi consentimiento para ser contactado por Revive respecto a mi solicitud de consulta.",
      submit: "Solicitar una consulta",
      submitting: "Enviando…",
      successTitle: "Gracias por contactar a Revive.",
      successBody: "Un miembro del equipo de atención de REVIVE te contactará para coordinar tu consulta.",
      waPrompt: "¿Prefieres WhatsApp? Chatea con nosotros ahora.",
      required: "Por favor completa los campos requeridos y el consentimiento.",
      selectPlaceholder: "Selecciona…",
    },
    intl: {
      chapter: "Pacientes Internacionales",
      title: "Visitando Revive desde los Estados Unidos",
      p1: "Revive se encuentra en Tijuana, Baja California, y da la bienvenida a pacientes que viajan desde los Estados Unidos e internacionalmente.",
      steps: ["Consulta", "Revisión médica", "Planificación del tratamiento", "Coordinación de viaje", "Tratamiento", "Seguimiento"],
      cta: "Habla con Nuestro Equipo de Atención",
    },
    footer: {
      slogan: "Ciencia Real. Resultados Reales. Revive Tu Vida.",
      brandLine: "En Revive combinamos la ciencia con la compasión.",
      healingLine: "Porque sanar no se trata del tratamiento. Se trata de la vida.",
      clinic: "Revive Clínica Médica Regenerativa",
      location: "Tijuana, Baja California, México",
      hours: "Horario de la Clínica",
      hoursPlaceholder: "Lunes – Sábado · 9:00 AM – 5:00 PM",
      addressPlaceholder: "Central Torreo · Av. Santa María, Calete, Int. 402",
      quickLinks: "Explorar",
      legal: "Legal",
      privacy: "Política de Privacidad", terms: "Términos de Uso", cookies: "Aviso de Cookies", disclaimerLink: "Aviso Médico",
      messageWa: "Enviar mensaje por WhatsApp",
      directions: "Cómo Llegar",
      directionsSub: "Una breve guía en video para llegar a la clínica.",
      disclaimer: "La información proporcionada en este sitio web tiene fines educativos e informativos y no pretende sustituir el consejo, diagnóstico o tratamiento médico profesional. Las recomendaciones de tratamiento se realizan tras una evaluación médica individual. Los resultados varían entre pacientes.",
      entities: "REVIVE Regenerative Medical Clinic es operada en México por REVIVE REGENERATIVE MEDICAL CLINIC, S. DE R.L. DE C.V. Las comunicaciones y ciertos servicios administrativos pueden ser proporcionados por REVIVE REGENERATIVE PARTNERSHIP LLC.",
      rights: "Todos los derechos reservados.",
    },
    wa: { desktop: "Chatea con Revive" },
    mobileCta: { book: "Agendar", wa: "WhatsApp" },
  },
};

export const WHATSAPP_NUMBER = "15553039140";
export const WHATSAPP_DISPLAY = "+1 (555) 303-9140";
export const PHONE_DISPLAY = "+1 (951) 631-9209";
export const PHONE_TEL = "tel:+19516319209";
